import { useDispatch } from 'react-redux';
import {
  Action,
  applyMiddleware,
  combineReducers,
  compose,
  createStore
} from 'redux';
import thunkMiddleware, { ThunkDispatch } from 'redux-thunk';
import alertsReducer from 'src/store/alerts/alerts.reducer';
import customersReducer from 'src/store/customers/customers.reducer';
import gradesReducer from 'src/store/grades/grades.reducer';
import instrumentGroupsReducer from 'src/store/instrumentgroups/instrumentgroups.reducer';
import instrumentsReducer from 'src/store/instruments/instruments.reducer';
import methodsReducer from 'src/store/methods/methods.reducer';
import mixtureCandidatesReducer from 'src/store/mixturecandidates/mixturecandidates.reducer';
import mixturesReducer from 'src/store/mixtures/mixtures.reducer';
import mixtureTypesReducer from 'src/store/mixturetypes/mixturetypes.reducer';
import packagingsReducer from 'src/store/packagings/packagings.reducer';
import palletsReducer from 'src/store/pallets/pallets.reducer';
import labelTemplatesReducer from 'src/store/labelTemplates/labelTemplates.reducer';
import parameterGroupsReducer from 'src/store/parametergroups/parametergroups.reducer';
import parametersReducer from 'src/store/parameters/parameters.reducer';
import parametersSetsReducer from 'src/store/parametersets/parametersets.reducer';
import plantsReducer from 'src/store/plants/plants.reducer';
import productsReducer from 'src/store/products/products.reducer';
import productTypesReducer from 'src/store/producttypes/producttypes.reducer';
import recipientsReducer from 'src/store/recipients/recipients.reducer';
import requestsReducer from 'src/store/requests/requests.reducer';
import runSystemsReducer from 'src/store/runsystems/runsystems.reducer';
import segmentsReducer from 'src/store/segments/segments.reducer';
import specificationsReducer from 'src/store/specifications/specifications.reducer';
import usersReducer from 'src/store/users/users.reducer';
import { AlertAction } from './alerts/alerts.actions';
import { CustomerAction } from './customers/customers.actions';
import { GradeAction } from './grades/grades.actions';
import { ImportAction } from './imports/imports.actions';
import importsReducer from './imports/imports.reducer';
import { InstrumentGroupAction } from './instrumentgroups/instrumentgroups.actions';
import { InstrumentAction } from './instruments/instruments.actions';
import { MethodAction } from './methods/methods.actions';
import { MixtureCandidateAction } from './mixturecandidates/mixturecandidates.actions';
import { MixtureAction } from './mixtures/mixtures.actions';
import { MixtureTypeAction } from './mixturetypes/mixturetypes.actions';
import { PackagingAction } from './packagings/packagings.actions';
import { PalletAction } from './pallets/pallets.actions';
import { LabelTemplateAction } from './labelTemplates/labelTemplates.actions';
import { ParameterGroupAction } from './parametergroups/parametergroups.actions';
import { ParameterAction } from './parameters/parameters.actions';
import { ParameterSetAction } from './parametersets/parametersets.actions';
import { persistStateMiddleware, recoverState } from './persistStoreMiddleware';
import { PlantAction } from './plants/plants.actions';
import { ProductAction } from './products/products.actions';
import { ProductTypeAction } from './producttypes/producttypes.actions';
import { RecipientAction } from './recipients/recipients.actions';
import { RequestAction } from './requests/requests.actions';
import { RunSystemAction } from './runsystems/runsystems.actions';
import { SegmentAction } from './segments/segments.actions';
import { SpecificationAction } from './specifications/specifications.actions';
import { UserAction } from './users/users.actions';

const rootReducer = combineReducers({
  requests: requestsReducer,
  alerts: alertsReducer,
  users: usersReducer,
  products: productsReducer,
  mixtures: mixturesReducer,
  mixtureCandidates: mixtureCandidatesReducer,
  mixtureTypes: mixtureTypesReducer,
  specifications: specificationsReducer,
  grades: gradesReducer,
  runSystems: runSystemsReducer,
  parameters: parametersReducer,
  parameterGroups: parameterGroupsReducer,
  parameterSets: parametersSetsReducer,
  methods: methodsReducer,
  instruments: instrumentsReducer,
  instrumentGroups: instrumentGroupsReducer,
  plants: plantsReducer,
  productTypes: productTypesReducer,
  segments: segmentsReducer,
  pallets: palletsReducer,
  labelTemplates: labelTemplatesReducer,
  packagings: packagingsReducer,
  customers: customersReducer,
  recipients: recipientsReducer,
  imports: importsReducer
});

export type StoreAction =
  | RequestAction
  | AlertAction
  | UserAction
  | ProductAction
  | MixtureAction
  | MixtureCandidateAction
  | MixtureTypeAction
  | SpecificationAction
  | GradeAction
  | RunSystemAction
  | ParameterAction
  | ParameterGroupAction
  | ParameterSetAction
  | MethodAction
  | InstrumentAction
  | InstrumentGroupAction
  | PlantAction
  | ProductTypeAction
  | SegmentAction
  | PalletAction
  | LabelTemplateAction
  | PackagingAction
  | CustomerAction
  | RecipientAction
  | ImportAction;

export type RootState = ReturnType<typeof rootReducer>;

export type Store = { getState: () => RootState; dispatch: () => void };

const composeEnhancers: Function =
  (window['__REDUX_DEVTOOLS_EXTENSION_COMPOSE__'] as Function) || compose;

export const configureStore = () => {
  const middlewares = [thunkMiddleware, persistStateMiddleware];
  const enhancer = composeEnhancers(applyMiddleware(...middlewares));
  return createStore(rootReducer, recoverState(), enhancer);
};

export type ReduxDispatch = ThunkDispatch<Store, any, Action>;
export function useReduxDispatch(): ReduxDispatch {
  return useDispatch<ReduxDispatch>();
}
